"use client";
import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { Body } from "../body";
import { CloseIcon } from "../icon/icons/close-icon";
import { TickIcon } from "../icon/icons/tick-icon";
import { Label } from "../label";

const textareaVariants = cva("", {
  variants: {
    status: {
      error: "",
      success: "",
      disabled: "disabled",
    },
  },
  defaultVariants: {
    status: null,
  },
});

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  placeholder: string;
  label?: string;
  message?: string;
  isRequired?: boolean;
}

const Textarea = ({
  className,
  label,
  status,
  isRequired,
  message,
  ref,
  ...props
}: TextareaProps & { ref?: React.RefObject<HTMLTextAreaElement | null> }) => (
  <div className={cn("flex flex-1 flex-col gap-1 font-paragraph", className)}>
    {label && (
      <Label
        className={cn("flex flex-row gap-1 py-1 font-bold")}
        htmlFor={label}
      >
        <Body
          className={cn(
            props.disabled ? "text-neutral-medium" : "text-neutral-darkest",
            "text-sm md:text-base lg:text-lg"
          )}
        >
          {label}
        </Body>
        {isRequired && (
          <Body
            className={cn(
              props.disabled ? "text-neutral-medium" : "text-neutral-dark",
              "text-sm md:text-base lg:text-lg"
            )}
          >
            *
          </Body>
        )}
      </Label>
    )}
    <div
      className={cn(
        "flex gap-2 border disabled:bg-neutral-light",
        status === "error" && "border-danger-medium",
        status === "success" && "border-success-medium",
        props.disabled && "bg-neutral-light",
        className
      )}
    >
      <textarea
        className={cn(
          "flex w-full bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          "text-sm md:text-base lg:text-lg",
          "min-h-24 md:min-h-20 lg:min-h-[70px]"
        )}
        ref={ref}
        {...props}
      />
    </div>
    <div>
      {status === "error" && (
        <span className="flex gap-1">
          <CloseIcon
            className="mt-2 h-3 w-3 rounded-full text-error"
            size="sm"
          />
          <Body className="text-error" size="xs">
            {message}
          </Body>{" "}
        </span>
      )}
      {status === "success" && (
        <span className="flex gap-1">
          <TickIcon className="mt-2 h-3 w-3 text-button-success" size="sm" />
          <Body className="text-button-success" size="xs">
            {message}
          </Body>{" "}
        </span>
      )}
      {props.disabled && message && (
        <span className="flex gap-1 text-neutral-medium">
          <Body size="xs">{message}</Body>
        </span>
      )}
      {!(props.disabled || status) && message && (
        <span className="flex gap-1">
          <Body size="xs">{message}</Body>
        </span>
      )}
    </div>
  </div>
);

Textarea.displayName = "Textarea";

export { Textarea };
